import * as React from 'react'
import { observer } from 'mobx-react'
import TaxRefundMethodViewModel from './../TaxRefundMethodViewModel'
import {
  CheckTaxRefundDeliveryMethod,
  DirectDepositTaxRefundDeliveryMethod,
  FutureTaxCreditTaxRefundDeliveryMethod,
  TaxRefundMethodVersion,
  TaxRefundType,
} from './../TaxRefundMethodVersion'

import { Section, SectionItem } from '../../components/Section'
import { PhysicalAddressSectionItems } from 'jobs/info-gathering/components/PhysicalAddress'
import SectionEmptyState from 'jobs/info-gathering/components/SectionEmptyState'
import EditRefundDialog from 'jobs/views/JobRoot/Job/CapturedInformationCard/EditRefundDialog'
import EditRefundDialogViewModel from 'jobs/views/JobRoot/Job/CapturedInformationCard/EditRefundDialog/EditRefundDialogViewModel'
import Button from 'components/Button'

interface Props {
  vm: TaxRefundMethodViewModel
  editVM?: EditRefundDialogViewModel
  canEdit?: boolean
}

export default observer(function RefundInfo({ vm, editVM, canEdit }: Props) {
  const editable = Boolean(canEdit && editVM)
  const hasPersonalSupportingItem = !!vm.personalRefundMethodSupportingItem
  const hasBusinessSupportingItem = !!vm.businessRefundMethodSupportingItem
  const bothSupportingItemsExist =
    hasPersonalSupportingItem && hasBusinessSupportingItem
  const bothEmpty =
    bothSupportingItemsExist &&
    !vm.personalRefundMethod &&
    !vm.businessRefundMethod

  if (!vm.personalRefundMethod && !vm.businessRefundMethod && !editable) {
    return <></>
  }

  return (
    <>
      {bothEmpty && editable && editVM ? (
        <SectionEmptyState
          title="No refund info yet"
          actionLabel="Fill in refund info"
          onAction={() => editVM.show('personal')}
          data-test="refund-empty-state-action"
        />
      ) : (
        <>
          {hasPersonalSupportingItem && (
            <>
              {vm.personalRefundMethod ? (
                <Section
                  title="Personal tax refund"
                  bordered
                  actions={
                    editable && editVM ? (
                      <Button
                        type="text"
                        onClick={() => editVM.show('personal')}
                        data-test="update-personal-refund-button"
                      >
                        Edit
                      </Button>
                    ) : undefined
                  }
                >
                  <SectionItem
                    label="Method"
                    value={vm.personalRefundMethodDeliveryMethodDisplay}
                  />
                  <RefundMethodSection vm={vm.personalRefundMethod} />
                </Section>
              ) : (
                editable &&
                editVM && (
                  <SectionEmptyState
                    title="No personal refund info yet"
                    actionLabel="Fill in personal refund info"
                    onAction={() => editVM.show('personal')}
                    data-test="personal-refund-empty-state-action"
                  />
                )
              )}
            </>
          )}
          {hasBusinessSupportingItem && (
            <>
              {vm.businessRefundMethod ? (
                <Section
                  title="Business tax refund"
                  bordered
                  actions={
                    editable && editVM ? (
                      <Button
                        type="text"
                        onClick={() => editVM.show('business')}
                        data-test="update-business-refund-button"
                      >
                        Edit
                      </Button>
                    ) : undefined
                  }
                >
                  <SectionItem
                    label="Method"
                    value={vm.businessRefundMethodDeliveryMethodDisplay}
                  />
                  <RefundMethodSection vm={vm.businessRefundMethod} />
                </Section>
              ) : (
                editable &&
                editVM && (
                  <SectionEmptyState
                    title="No business refund info yet"
                    actionLabel="Fill in business refund info"
                    onAction={() => editVM.show('business')}
                    data-test="business-refund-empty-state-action"
                  />
                )
              )}
            </>
          )}
        </>
      )}
      {editVM && <EditRefundDialog vm={editVM} />}
    </>
  )
})

interface RefundMethodSectionProps {
  vm: TaxRefundMethodVersion
}

function RefundMethodSection({ vm }: RefundMethodSectionProps) {
  return (
    <>
      {vm.deliveryMethod instanceof CheckTaxRefundDeliveryMethod && (
        <CheckTaxRefundDeliveryMethodSection
          vm={vm.deliveryMethod}
          refundType={vm.type}
        />
      )}

      {vm.deliveryMethod instanceof DirectDepositTaxRefundDeliveryMethod && (
        <DirectDepositTaxRefundDeliveryMethodSection vm={vm.deliveryMethod} />
      )}

      {vm.deliveryMethod instanceof FutureTaxCreditTaxRefundDeliveryMethod && (
        <FutureTaxCreditTaxRefundDeliveryMethodSection />
      )}
    </>
  )
}

interface CheckTaxRefundDeliveryMethodSectionProps {
  vm: CheckTaxRefundDeliveryMethod
  refundType?: TaxRefundType
}

function CheckTaxRefundDeliveryMethodSection({
  vm,
  refundType,
}: CheckTaxRefundDeliveryMethodSectionProps) {
  if (vm.mailingAddress) {
    return <PhysicalAddressSectionItems vm={vm.mailingAddress} />
  }

  if (refundType === TaxRefundType.Personal) {
    return (
      <SectionItem
        label="Address"
        value="Using same address as personal information."
        copyable={false}
      />
    )
  }

  if (refundType === TaxRefundType.Business) {
    return (
      <SectionItem
        label="Address"
        value="Using same address as business information."
        copyable={false}
      />
    )
  }

  return <></>
}

interface DirectDepositTaxRefundDeliveryMethodSectionProps {
  vm: DirectDepositTaxRefundDeliveryMethod
}

function DirectDepositTaxRefundDeliveryMethodSection({
  vm,
}: DirectDepositTaxRefundDeliveryMethodSectionProps) {
  return (
    <>
      <SectionItem
        label="Account type"
        value={vm.bankAccount?.accountType}
        isSensitive
      />

      <SectionItem
        label="Routing number"
        value={vm.bankAccount?.routingNumber}
        maskedValue={vm.bankAccount?.routingNumberDisplay}
        isSensitive
      />

      <SectionItem
        label="Account number"
        value={vm.bankAccount?.accountNumber}
        maskedValue={vm.bankAccount?.accountNumberDisplay}
        isSensitive
      />

      <SectionItem
        label="Bank name"
        value={vm.bankAccount?.bankName}
        maskedValue={vm.bankAccount?.bankNameDisplay}
        isSensitive
        showHideLabel="name"
      />
    </>
  )
}

function FutureTaxCreditTaxRefundDeliveryMethodSection() {
  return <></>
}
